import { isEmpty } from 'ramda';
import React, { FC, useCallback, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { Loader, Text, TextButton } from '../../../components/common';
import { HomeButtonName, useTrackMediaHomeAnalytics } from '../../../utils/hooks/bi/useTrackMediaHomeAnalytics';
import { useBoolean } from '../../../utils/hooks/useBoolean';
import { LoadingSection } from '../LoadingSection';
import { HorizontalSection } from '../../../components/common/HorizontalSection';
import { Recommended } from './Recommended';
import styles from './RecommendedSection.scss';
import { IOuter } from './RecommendedSection.types';
import { useMMGRSettings } from '../../../utils/hoc/withMMGRSettings';
import { useRecommendedCategoriesModalHandlers } from '../../Root/hooks/useRecommendedCategoriesModal';
import { useAdapter } from '../../../utils/hooks/useAdapter';
import { MainSource } from '../../../constants/Source';
import { useAsync } from 'react-use';
import { getLocale } from '../../../utils/env/getLocale';
import { captureException } from '@sentry/react';

export const RecommendedSection: FC<IOuter> = ({
  isLoading,
  openExpandedView,
  buyItem,
  onItemClick,
  onItemDoubleClick,
  isItemSelected,
  selectedItems,
  isSecure,
  beginDrag,
  publicMediaCurrentCategory,
  items,
  isRecommendedCategoriesLoading,
  onItemRightClick,
  recommendedCategories,
  loadingError,
  selectRecommendedCategories,
  dataHook,
  currentFolder,
}) => {
  const {
    mmgrSettings: { isUIAlignmentsEnabled },
  } = useMMGRSettings();
  const { trackRecommendedButtonClick } = useTrackMediaHomeAnalytics();
  const { value: didCategoriesChange, setTrue: markCategoriesAsChanged } = useBoolean();
  const { value: shouldRenderSectionLoader, setFalse: renderUpdatingLoader } = useBoolean(true);

  useEffect(() => {
    if (!didCategoriesChange) {
      return;
    }

    renderUpdatingLoader();
  }, [recommendedCategories, didCategoriesChange]);

  const homeAdapter = useAdapter(MainSource.Home);
  const recommendedTopicsLoadingState = useAsync(() => homeAdapter.topics.list({ locale: getLocale() }), []);
  const emptyRecommendedTopicsList = [];
  if (recommendedTopicsLoadingState.error) {
    captureException(
      new Error('[recommended]: Error during fetching topics', {
        cause: recommendedTopicsLoadingState.error,
      }),
    );
  }

  const recommendedTopics =
    recommendedTopicsLoadingState.loading || recommendedTopicsLoadingState.error
      ? emptyRecommendedTopicsList
      : recommendedTopicsLoadingState.value.data;

  const hasSavedRecommendedCategories = !isEmpty(recommendedCategories);

  const { openModal: openRecommendedCategoriesModal } = useRecommendedCategoriesModalHandlers();

  const handleConfigurationClick = useCallback(() => {
    openRecommendedCategoriesModal({
      recommendedTopics,
      updateTopics: selectRecommendedCategories,
    });
    trackRecommendedButtonClick(recommendedCategories, HomeButtonName.Settings);
  }, [trackRecommendedButtonClick, recommendedTopics, recommendedCategories, isRecommendedCategoriesLoading]);

  const handleNavigationLeftButtonClick = useCallback(
    () => trackRecommendedButtonClick(recommendedCategories, HomeButtonName.NavLeft),
    [trackRecommendedButtonClick, recommendedCategories],
  );

  const handleNavigationRightButtonClick = useCallback(
    () => trackRecommendedButtonClick(recommendedCategories, HomeButtonName.NavRight),
    [trackRecommendedButtonClick, recommendedCategories],
  );

  const isShowingLoader = isRecommendedCategoriesLoading || isLoading;

  const recommendedItems = !loadingError && (
    <HorizontalSection
      header={
        <>
          <Text
            weight="bold"
            size={isUIAlignmentsEnabled ? 'small' : 'medium'}
            className={styles.recommendedItemsHeaderText}
            tagName="div"
            dataHook="recommended-section-header"
          >
            <FormattedMessage
              id={
                hasSavedRecommendedCategories
                  ? 'media-home.recommendation-section.items.recommended.header'
                  : 'media-home.recommendation-section.items.popular.header'
              }
            />
          </Text>

          <Text weight="thin" size="small" tagName="div">
            <FormattedMessage
              id={
                hasSavedRecommendedCategories
                  ? 'media-home.recommendation-section.items.recommended.subtitle'
                  : 'media-home.recommendation-section.items.popular.subtitle'
              }
              values={{
                editTopics: (
                  <TextButton
                    size="small"
                    weight="normal"
                    onClick={handleConfigurationClick}
                    dataHook="edit-topics-button"
                  >
                    <FormattedMessage
                      id={
                        hasSavedRecommendedCategories
                          ? 'media-home.recommendation-section.items.recommended.subtitle.edit'
                          : 'media-home.recommendation-section.items.popular.subtitle.edit'
                      }
                    />
                  </TextButton>
                ),
              }}
            />
          </Text>
        </>
      }
    >
      {!isEmpty(items) && (
        <div className={styles.recommendedSectionItems}>
          <Recommended
            dataHook={dataHook}
            openExpandedView={openExpandedView}
            buyItem={buyItem}
            onItemClick={onItemClick}
            onItemDoubleClick={onItemDoubleClick}
            isItemSelected={isItemSelected}
            selectedItems={selectedItems}
            isSecure={isSecure}
            beginDrag={beginDrag}
            publicMediaCurrentCategory={publicMediaCurrentCategory}
            items={items}
            onNavLeftClick={handleNavigationLeftButtonClick}
            onNavRightClick={handleNavigationRightButtonClick}
            isLoading={isLoading}
            onItemRightClick={onItemRightClick}
            data-hook="items"
            currentFolder={currentFolder}
          />
        </div>
      )}
    </HorizontalSection>
  );

  const loaderType = shouldRenderSectionLoader ? (
    <HorizontalSection>
      <LoadingSection
        headerText={<FormattedMessage id="media-home.recommendation-section.items.recommended.header" />}
      />
    </HorizontalSection>
  ) : (
    <HorizontalSection
      header={
        <Text weight="bold" size="medium" className={styles.loaderHeader}>
          <FormattedMessage id="media-home.recommendation-section.items.recommended.header" />
        </Text>
      }
    >
      <div className={styles.loader} data-hook="updating-recommended-loader">
        <Loader />
        <Text weight="normal" size="small" className={styles.loaderText}>
          <FormattedMessage id="media-home.recommendation-section.loader-text" />
        </Text>
      </div>
    </HorizontalSection>
  );

  return isShowingLoader ? loaderType : recommendedItems;
};
